/**
 * Screenplay dialogue parser.
 *
 * Extracts dialogue lines from a plain-text shot description that follows
 * standard screenplay formatting:
 *
 *   TARA
 *   Hey, can you make me a caramel latte?
 *
 *   KABIR
 *   No. I wanted money for the latte you just ordered.
 *
 *   NARRATOR (V.O.)
 *   She walks in, unaware.
 *
 * Rules:
 *   • Character CUE is an ALL-CAPS token on its own line — optionally
 *     followed by a parenthetical like "(V.O.)", "(O.S.)", "(CONT'D)".
 *   • Dialogue TEXT is the next 1+ lines until a blank line, another CUE,
 *     a scene heading ("INT.", "EXT."), or an action line that starts
 *     with a lowercase letter.
 *   • Character names may span multiple words ("MR. KHAN", "OLD MAN").
 *
 * Returns an ordered list of { character, text, index } objects. `index`
 * preserves the reading order so the same character speaking twice keeps
 * its temporal ordering.
 */

// Skip these ALL-CAPS lines — they're scene / transition markers, not CUEs.
const NON_CUE_KEYWORDS = new Set([
    'INT.', 'EXT.', 'INT./EXT.', 'EXT./INT.', 'I/E.',
    'FADE IN:', 'FADE OUT.', 'FADE OUT:', 'FADE TO:', 'CUT TO:',
    'DISSOLVE TO:', 'SMASH CUT:', 'MATCH CUT:',
    'SCENE', 'ACT',
]);

/**
 * True when a line looks like a bare character CUE (on its own line,
 * no dialogue text following on the same line):
 *   • Trimmed content is ALL CAPS (allows spaces, dots, apostrophes,
 *     ampersands and a trailing parenthetical).
 *   • Contains at least one A-Z letter.
 *   • Is not a scene heading / transition marker.
 */
const isCueLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.length < 2 || trimmed.length > 60) return false;

    // Strip a trailing "(V.O.)" / "(voice over)"-style parenthetical.
    const nameOnly = trimmed.replace(/\s*\([^)]*\)\s*$/, '').trim();
    if (!nameOnly) return false;

    // Must be all-caps letters (with optional dots / apostrophes / spaces / &).
    if (!/^[A-Z][A-Z0-9 .'&\-\/]*$/.test(nameOnly)) return false;

    // Must contain at least 2 letters.
    if ((nameOnly.match(/[A-Z]/g) || []).length < 2) return false;

    // Exclude known non-cue markers.
    const first = nameOnly.split(/\s+/)[0];
    if (NON_CUE_KEYWORDS.has(first) || NON_CUE_KEYWORDS.has(nameOnly)) return false;
    if (/^SCENE\s+\d+/.test(nameOnly)) return false;

    return true;
};

/**
 * Try to match the INLINE dialogue pattern:
 *   NAME[ (parenthetical)]?\s*:\s*dialogue text
 * Examples that must match:
 *   TARA: Hey!
 *   TARA (V.O.): Hey!
 *   TARA (voice over) : Hey!
 *   KABIR (voice over):What are you doing?
 *   MR. KHAN: Hello.
 *
 * Returns { character, parenthetical, dialogue } or null.
 * The character MUST be recognisable as a CUE (all-caps).
 */
const matchInlineCueLine = (line) => {
    const trimmed = String(line || '').trim();
    if (!trimmed) return null;
    // Group 1: CUE (all caps + allowed punct), Group 2: optional parenthetical,
    // Group 3: dialogue.
    const m = trimmed.match(
        /^([A-Z][A-Z0-9 .'&\-\/]{0,58}?)\s*(\([^)]*\))?\s*:\s*(\S.*)$/
    );
    if (!m) return null;
    const character = m[1].trim();
    const parenthetical = m[2] ? m[2].slice(1, -1).trim() : '';
    const dialogue = m[3].trim();

    // Sanity: character must be truly all-caps and >= 2 letters.
    if (!/^[A-Z][A-Z0-9 .'&\-\/]*$/.test(character)) return null;
    if ((character.match(/[A-Z]/g) || []).length < 2) return null;

    // Exclude scene headings / transitions
    const first = character.split(/\s+/)[0];
    if (NON_CUE_KEYWORDS.has(first) || NON_CUE_KEYWORDS.has(character)) return null;

    // Guard against action lines that start with a proper noun followed by a
    // colon — e.g. "NOTE: something". Character CUEs are usually >= 3 letters
    // and don't include common note prefixes.
    if (/^(NOTE|TODO|WARNING|BEAT|ACTION)\b/.test(character)) return null;

    return { character, parenthetical, dialogue };
};

/**
 * Detect the V.O./O.S./voice-over flag from a parenthetical string.
 * Accepts: V.O., VO, V. O., O.S., OS, voice over, voice-over, VOICEOVER,
 * off screen, off-screen, offscreen.
 */
const detectVOFlag = (parenthetical) => {
    if (!parenthetical) return false;
    return /(?:^|\s|[-.])(?:V\.?\s*O\.?|O\.?\s*S\.?|voice[\s-]*over|voiceover|off[\s-]*screen|offscreen|narration|narrator)(?:\s|[-.,)]|$)/i
        .test(` ${parenthetical} `);
};

/**
 * Parse dialogues out of a raw shot description.
 *
 * @param {string} description
 * @returns {Array<{ character: string, text: string, index: number, isVO: boolean }>}
 */
export const parseDialoguesFromDescription = (description) => {
    if (!description || typeof description !== 'string') return [];

    const lines = description.split(/\r?\n/);
    const out = [];
    let i = 0;
    let index = 0;

    while (i < lines.length) {
        const line = lines[i];

        // Try INLINE form first: NAME[(paren)]?: dialogue on the SAME line.
        const inline = matchInlineCueLine(line);
        if (inline) {
            const { character, parenthetical, dialogue } = inline;
            const isVO = detectVOFlag(parenthetical);
            const textParts = [dialogue];
            i += 1;
            // Collect wrap lines (same-CUE continuation).
            while (i < lines.length) {
                const t = lines[i];
                if (!t.trim()) break;
                if (isCueLine(t)) break;
                if (matchInlineCueLine(t)) break;
                const scan = t.trim();
                const upperFirst = scan.split(/\s+/)[0];
                if (NON_CUE_KEYWORDS.has(upperFirst)) break;
                const prev = textParts[textParts.length - 1] || '';
                const prevTerminated = /[.!?]\s*$/.test(prev);
                const startsCapital = /^[A-Z]/.test(scan);
                const wordCount = scan.split(/\s+/).length;
                if (prevTerminated && startsCapital && wordCount >= 4) break;
                const stripped = t.replace(/^\s*\([^)]*\)\s*/, '');
                if (stripped.trim()) textParts.push(stripped.trim());
                i += 1;
            }
            const text = textParts.join(' ').replace(/\s+/g, ' ').trim();
            if (text) {
                out.push({ character, text, index, isVO });
                index += 1;
            }
            continue;
        }

        // Otherwise fall through to BARE-CUE form: CUE on its own line,
        // dialogue text on subsequent lines.
        if (!isCueLine(line)) {
            i += 1;
            continue;
        }
        const cueRaw = line.trim();
        const parenMatch = cueRaw.match(/\(([^)]+)\)\s*$/);
        const isVO = detectVOFlag(parenMatch ? parenMatch[1] : '');
        const character = cueRaw
            .replace(/\s*\([^)]*\)\s*$/, '')
            .replace(/\s+/g, ' ')
            .trim();
        if (!character) { i += 1; continue; }

        // Collect dialogue text on the following lines until:
        //   • blank line
        //   • another CUE line (bare OR inline)
        //   • a scene heading
        //   • or a new prose/action paragraph.
        const textParts = [];
        i += 1;
        while (i < lines.length) {
            const t = lines[i];
            if (!t.trim()) break;
            if (isCueLine(t)) break;
            if (matchInlineCueLine(t)) break;
            const scan = t.trim();
            const upperFirst = scan.split(/\s+/)[0];
            if (NON_CUE_KEYWORDS.has(upperFirst)) break;

            const prev = textParts[textParts.length - 1] || '';
            const prevTerminated = /[.!?]\s*$/.test(prev);
            const startsCapital = /^[A-Z]/.test(scan);
            const wordCount = scan.split(/\s+/).length;
            if (prevTerminated && startsCapital && wordCount >= 4) break;

            const stripped = t.replace(/^\s*\([^)]*\)\s*/, '');
            if (stripped.trim()) textParts.push(stripped.trim());
            i += 1;
        }

        const text = textParts.join(' ').replace(/\s+/g, ' ').trim();
        if (text) {
            out.push({ character, text, index, isVO });
            index += 1;
        }
    }

    return out;
};

/**
 * Given a list of raw dialogues and the project's character library,
 * resolve each dialogue's `characterId` by name-matching (case-insensitive,
 * whole-word). Falls back to the first character with matching category
 * when the cue name has no exact match.
 *
 * @param {Array} rawDialogues — output of parseDialoguesFromDescription
 * @param {Array<{ id, name, category }>} projectCharacters
 * @returns {Array<{ characterId, characterName, text, order, index, isVO }>}
 */
export const resolveDialogueCharacterIds = (rawDialogues, projectCharacters = []) => {
    if (!Array.isArray(rawDialogues) || rawDialogues.length === 0) return [];
    const chars = Array.isArray(projectCharacters) ? projectCharacters : [];

    const findCharByName = (rawName) => {
        const clean = rawName.replace(/\s+/g, ' ').trim().toLowerCase();
        if (!clean) return null;
        // Exact case-insensitive match
        const exact = chars.find(c => (c?.name || '').trim().toLowerCase() === clean);
        if (exact) return exact;
        // "TARA" -> "Tara Smith": match first-word
        const firstWord = clean.split(' ')[0];
        const partial = chars.find(c => {
            const cn = (c?.name || '').trim().toLowerCase();
            return cn === firstWord || cn.startsWith(firstWord + ' ') || cn.endsWith(' ' + firstWord);
        });
        if (partial) return partial;
        // Narrator special-case: any character with category === 'narrator'
        if (/NARRATOR|VOICE|V\.?O\.?/i.test(rawName)) {
            const narrator = chars.find(c => c?.category === 'narrator');
            if (narrator) return narrator;
        }
        return null;
    };

    const out = [];
    rawDialogues.forEach((d, i) => {
        const match = findCharByName(d.character);
        out.push({
            characterId: match?.id || null,
            characterName: match?.name || d.character,
            text: d.text,
            order: i,
            index: i,
            isVO: !!d.isVO,
            // Preserve the raw cue so callers can create a character stub
            // when no match is found (e.g. "TARA" mentioned but not yet
            // added to the project's cast).
            rawCue: d.character,
        });
    });
    return out;
};

export default parseDialoguesFromDescription;
